import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import Collapse from "@mui/material/Collapse";

import Box from "components/common/Box";
import Stack from "components/common/Stack";
import Text from "components/common/Text";
import Button from "components/common/Button";
import IconButton from "components/common/IconButton";
import AllInOneFooter from "components/allInOneDashboard/AllInOneFooter";
import AtlasPricingSection from "components/Product-Info-Pages/Atlas/AtlasPricingSection";
import { UNIFIED_DASHBOARD_PATH } from "components/allInOneDashboard/constants/productRouteMap";
import { config } from "config/index";
import { localStore } from "stores/localStorage";
import { thirdpartyApis } from "stores/thirdparty/thirdpartyApis";
import { thirdpartyParsers } from "stores/thirdparty/thirdpartyParsers";

const ACCENT = "#9C430C";
const FACT_DOT = "#C06A26";
const HERO_GRADIENT =
  "linear-gradient(162deg, #0B0F14 1.5%, #1A0F0C 35.4%, #2B1612 69.4%, #4A2A1A 98.5%)";
const HERO_GRADIENT_MOBILE =
  "linear-gradient(136deg, #0B0F14 1.5%, #1A0F0C 35.4%, #2B1612 69.4%, #4A2A1A 98.5%)";
const CTA_GRADIENT = "linear-gradient(270deg, #EC761F 0%, #A46428 100%)";
const FOR_YOU_GRADIENT = "linear-gradient(-26deg, #E57421 1.4%, #422810 98.6%)";
const MARKETS_CARD_BG = "#1A120B";
const FEATURE_BORDER = "#FFE6CD";
const FACT_BORDER = "#1F2937";

const img = (name) => `/images/atlas-product-info/${encodeURIComponent(name)}`;

const ICONS = {
  hero: img("Atlas.png"),
  frame: img("Frame.svg"),
  frameWhite: img("Frame-white.svg"),
  check: img("icon.svg"),
  tech1: img("technology-_1_ 1.svg"),
  dashboard: img("dashboard 1.svg"),
  clock: img("clock 2.svg"),
  tech2: img("technology-_2_ 1.svg"),
  tracking: img("tracking 1.svg"),
  momentum: img("momentum 1.svg"),
  consistency: img("consistency 1.svg"),
  noStopping: img("no-stopping 1.svg"),
  benefit1: img("icon-circle.svg"),
  benefit2: img("icon-circle (1).svg"),
  benefit3: img("icon-circle (2).svg"),
  anchor: img("typographic-anchor.svg"),
  arrowWhite: "/images/atlas-product-info/pricing/arrow-solid.svg",
};

const ArrowIcon = ({ size = 24 }) => (
  <Box
    component="img"
    src={ICONS.arrowWhite}
    alt=""
    sx={{ width: size, height: size, display: "block" }}
  />
);

const BENEFIT_TRAY = [
  { icon: ICONS.benefit1, label: "Access 10+ Global markets" },
  { icon: ICONS.benefit2, label: "Trade equities & commodities" },
  { icon: ICONS.benefit3, label: "High Conviction Trade Ideas" },
];

const FEATURE_CARDS = [
  {
    icon: ICONS.tech1,
    text: "Access research across the US, UK, Germany, China, India and other major economies.",
  },
  {
    icon: ICONS.dashboard,
    text: "Complete Trade Setup: Ticker | Entry Price | Target (s) | Stop Loss | Research Rationale",
  },
  {
    icon: ICONS.clock,
    text: "Professional-quality reports designed to simplify global investing decisions.",
  },
  {
    icon: ICONS.tech2,
    text: "Explore equities, ETFs, commodities and thematic investment ideas from one platform.",
  },
  {
    icon: ICONS.tracking,
    text: (
      <>
        Track opportunities across Technology, Healthcare,
        <br />
        Energy, Financials, Consumer and
        <br />
        Industrial sectors.
      </>
    ),
  },
  {
    icon: ICONS.momentum,
    text: "Receive updated research as markets evolve and investment conditions change.",
  },
  {
    icon: ICONS.consistency,
    text: "Identify emerging megatrends shaping the future of investing.",
  },
  {
    icon: ICONS.noStopping,
    text: "Designed for investors seeking diversified global exposure with AI-powered research and long-term investment insights.",
    fullWidth: true,
  },
];

const FOR_YOU_CARDS = [
  {
    label: "Global Investing",
    description: "Looking to diversify beyond domestic markets.",
  },
  {
    label: "AI Research",
    description: "Prefer data-driven investment decisions over market speculation.",
  },
  {
    label: "Diversification",
    description: "Want exposure across multiple countries and sectors.",
  },
  {
    label: "Long Term Wealth",
    description: "Focused on building wealth through global opportunities.",
  },
  {
    label: "Premium Insights",
    description: "Need institutional-quality research in a simplified format.",
  },
  {
    label: "Continuous Tracking",
    description: "Want research that evolves with changing market conditions.",
  },
];

const QUICK_FACTS = [
  { label: "Focus", value: "Global Investing" },
  { label: "Research", value: "AI Powered" },
  { label: "Coverage", value: "10+ Global Markets" },
  { label: "Asset Classes", value: "Stocks | ETFs | Commodities" },
  { label: "Investor Type", value: "Long Term Investors" },
  {
    label: "Trade Setup",
    value: "Ticker | Entry Price | Target (s) | Stop Loss | Research Rationale",
  },
];

const MOBILE_ABOUT_ITEMS = [
  {
    icon: ICONS.frame,
    text: "Access research across the US, UK, Germany, China, India and other major economies.",
  },
  {
    icon: ICONS.dashboard,
    text: "Complete Trade Setup: Ticker | Entry Price | Target (s) | Stop Loss | Research Rationale",
  },
  {
    icon: ICONS.clock,
    text: "Professional quality reports designed to simplify global investing decisions.",
  },
  {
    icon: ICONS.tech2,
    text: "Explore equities, ETFs, commodities and thematic investment ideas from one platform.",
  },
  {
    icon: ICONS.tracking,
    text: "Track opportunities across technology, healthcare, energy, financials, consumer, and industrial sectors.",
  },
  {
    icon: ICONS.momentum,
    text: "Receive updated research as markets evolve and investment conditions change.",
  },
  {
    icon: ICONS.consistency,
    text: "Identify emerging megatrends shaping the future of investing.",
  },
  {
    icon: ICONS.noStopping,
    text: "Designed for investors seeking diversified global exposure with AI powered and long term investment insights.",
  },
];

const MOBILE_FOR_YOU = [
  "Looking to diversify beyond domestic markets",
  "Prefer data driven investment decisions over market speculation",
  "Want exposure across multiple countries and sectors",
  "Focused on building wealth through global opportunities",
  "Need institutional quaity research",
  "Want research that evolves with changing market conditions",
];

const MOBILE_ABOUT_PREVIEW = 2;

const pagePad = {
  px: { xs: 2.5, md: 7.5 },
  maxWidth: 1320,
  mx: "auto",
  width: "100%",
};

const FeatureCard = ({ icon, text, sx = {} }) => (
  <Stack
    spacing="12px"
    sx={{
      p: "32px",
      height: "100%",
      minWidth: 0,
      width: "100%",
      boxSizing: "border-box",
      borderRadius: "20px",
      bgcolor: "#fff",
      border: `1px solid ${FEATURE_BORDER}`,
      boxShadow: "0 8px 9px rgba(0,0,0,0.15)",
      ...sx,
    }}
  >
    <Box
      component="img"
      src={icon}
      alt=""
      sx={{ width: 32, height: 32, flexShrink: 0 }}
    />
    <Text
      sx={{
        width: "100%",
        minWidth: 0,
        fontSize: 18,
        fontWeight: 500,
        color: "#000",
        lineHeight: "26px",
        fontFamily: "'DM Sans', sans-serif",
        wordBreak: "break-word",
        overflowWrap: "break-word",
      }}
    >
      {text}
    </Text>
  </Stack>
);

const ChecklistCard = ({ label, description }) => (
  <Stack
    spacing={1.5}
    sx={{
      p: 3,
      height: "100%",
      borderRadius: "12px",
      bgcolor: "#F8FAFC",
      border: "1px solid #E5E7EB",
      boxShadow:
        "0 4px 14px -8px rgba(156,67,12,0.08), 0 10px 24px -12px rgba(0,0,0,0.07)",
      position: "relative",
      overflow: "hidden",
    }}
  >
    <Box
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: 3,
        bgcolor: ACCENT,
      }}
    />
    <Stack direction="row" spacing={1.5} alignItems="center">
      <Box component="img" src={ICONS.check} alt="" sx={{ width: 36, height: 36 }} />
      <Text sx={{ fontSize: 15, fontWeight: 600, color: "#111827", lineHeight: "20px" }}>
        {label}
      </Text>
    </Stack>
    <Text sx={{ fontSize: 13, color: "#6B7280", lineHeight: "20px" }}>{description}</Text>
  </Stack>
);

const FactCard = ({ label, value }) => (
  <Stack
    spacing={1.5}
    sx={{
      p: 3,
      height: "100%",
      borderRadius: "16px",
      bgcolor: "#0B1220",
      border: `1px solid ${FACT_BORDER}`,
      boxShadow: "0 6px 8px rgba(0,0,0,0.2), 0 18px 20px rgba(0,0,0,0.4)",
    }}
  >
    <Stack direction="row" spacing={1.25} alignItems="center">
      <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: FACT_DOT }} />
      <Text sx={{ fontSize: 18, fontWeight: 600, color: "#94A3B8" }}>{label}</Text>
    </Stack>
    <Text sx={{ fontSize: 26, fontWeight: 700, color: "#F8FAFC", lineHeight: 1.3 }}>
      {value}
    </Text>
  </Stack>
);

const MobileAboutRow = ({ icon, text }) => (
  <Stack direction="row" spacing={1.5} alignItems="flex-start">
    <Box
      component="img"
      src={icon}
      alt=""
      sx={{ width: 20, height: 20, flexShrink: 0, mt: "2px" }}
    />
    <Text sx={{ fontSize: 14, color: "#475569", lineHeight: "21px" }}>{text}</Text>
  </Stack>
);

const MobileForYouRow = ({ text }) => (
  <Stack
    direction="row"
    spacing={2}
    alignItems="center"
    sx={{
      p: 2,
      minHeight: 63,
      borderRadius: "16px",
      bgcolor: "rgba(255,255,255,0.12)",
      border: "1px solid rgba(255,255,255,0.12)",
      boxShadow: "0 10px 12px rgba(0,0,0,0.1)",
      backdropFilter: "blur(7px)",
    }}
  >
    <Box
      sx={{
        width: 24,
        height: 24,
        flexShrink: 0,
        display: "grid",
        placeItems: "center",
      }}
    >
      <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: FACT_DOT }} />
    </Box>
    <Text sx={{ fontSize: 14, fontWeight: 500, color: "#fff", lineHeight: 1.35 }}>{text}</Text>
  </Stack>
);

const MobileFactTile = ({ label, value }) => (
  <Stack
    spacing={1.25}
    sx={{
      flex: 1,
      minWidth: 0,
      minHeight: 90,
      p: 2,
      borderRadius: "16px",
      bgcolor: "#fff",
      border: "1px solid #E2E8F0",
      boxShadow: "0 4px 6px rgba(0,0,0,0.04)",
    }}
  >
    <Stack direction="row" spacing={1.25} alignItems="center">
      <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: FACT_DOT }} />
      <Text sx={{ fontSize: 12, color: "#000", lineHeight: "16px" }}>{label}</Text>
    </Stack>
    <Text sx={{ fontSize: 16, fontWeight: 700, color: "#0F172A", lineHeight: "24px" }}>
      {value}
    </Text>
  </Stack>
);

const AtlasProductInfo = () => {
  const history = useHistory();
  const [aboutExpanded, setAboutExpanded] = useState(false);
  const [plans, setPlans] = useState([]);

  const atlasProductCode = config.productCodes?.ATLAS || "ATLAS";

  useEffect(() => {
    thirdpartyApis
      .getSubscriptionPlans({ productCode: atlasProductCode })
      .then((res) => setPlans(thirdpartyParsers.subscriptionPlans(res) || []))
      .catch(() => setPlans([]));
  }, [atlasProductCode]);

  const handleStart = () => {
    if (config.productCodes?.ATLAS) {
      localStore.setActiveProduct(config.productCodes.ATLAS);
    }
    const atlasLoginUrl =
      config.atlasLoginUrl ||
      (config.environment === "PRODUCTION"
        ? "https://atlas.jarvisinvest.com/login"
        : "https://dev-atlas.jarvisinvest.com/login");
    window.open(atlasLoginUrl, "_blank", "noopener,noreferrer");
  };

  const handleBack = () => {
    history.push(UNIFIED_DASHBOARD_PATH);
  };

  const middleFeatures = FEATURE_CARDS.slice(1, -1);
  const fullWidthFeature = FEATURE_CARDS[FEATURE_CARDS.length - 1];
  const aboutPreview = MOBILE_ABOUT_ITEMS.slice(0, MOBILE_ABOUT_PREVIEW);
  const aboutRest = MOBILE_ABOUT_ITEMS.slice(MOBILE_ABOUT_PREVIEW);

  return (
    <Box sx={{ bgcolor: "#fff" }}>
      {/* Hero — Figma 8757:21600 (1440 × 640) */}
      <Box
        sx={{
          position: "relative",
          background: { xs: HERO_GRADIENT_MOBILE, md: HERO_GRADIENT },
          overflow: "hidden",
          borderRadius: { xs: "0 0 24px 24px", md: 0 },
          boxShadow: {
            xs: "0 12px 28px -10px rgba(74,42,26,0.25)",
            md: "none",
          },
        }}
      >
        {/* Desktop — absolute layout matching Figma */}
        <Box
          sx={{
            display: { xs: "none", md: "block" },
            position: "relative",
            height: 640,
            maxWidth: 1440,
            mx: "auto",
            boxSizing: "border-box",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              left: -200,
              top: -100,
              width: 800,
              height: 800,
              borderRadius: "50%",
              background:
                "radial-gradient(circle, rgba(236,132,64,0.18) 0%, rgba(11,15,20,0) 70%)",
              pointerEvents: "none",
            }}
          />

          <Stack
            direction="row"
            alignItems="center"
            spacing={1.5}
            sx={{ position: "absolute", left: 60, top: 32, zIndex: 2 }}
          >
            <IconButton
              onClick={handleBack}
              aria-label="Back to Explore"
              sx={{
                width: 42,
                height: 42,
                borderRadius: "12px",
                bgcolor: "rgba(255,255,255,0.2)",
                border: "1px solid #C3C3C3",
                boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)",
                p: 0,
                "&:hover": { bgcolor: "rgba(255,255,255,0.35)" },
              }}
            >
              <ArrowBackRoundedIcon sx={{ fontSize: 24, color: "#fff" }} />
            </IconButton>
            <Stack spacing={1.1}>
              <Text
                sx={{
                  fontSize: 32,
                  fontWeight: 800,
                  color: "#fff",
                  lineHeight: "26px",
                  fontFamily: "'DM Sans', sans-serif",
                }}
              >
                Jarvis Atlas
              </Text>
              <Text
                sx={{
                  fontSize: 14,
                  fontWeight: 400,
                  color: "rgba(255,255,255,0.7)",
                  lineHeight: "20px",
                  fontFamily: "'DM Sans', sans-serif",
                }}
              >
                Investing powered by AI
              </Text>
            </Stack>
          </Stack>

          {/* Content Left — Figma 8757:21610 */}
          <Box
            sx={{
              position: "absolute",
              left: 60,
              top: 141,
              width: 624,
              height: 358,
              zIndex: 2,
            }}
          >
            <Stack spacing={3} sx={{ width: "100%" }}>
              {/* Headline is 862px in Figma — overflows toward compass, exactly 2 lines */}
              <Box sx={{ width: 862, maxWidth: "none" }}>
                <Text
                  sx={{
                    fontSize: 64,
                    fontWeight: 800,
                    color: "#fff",
                    lineHeight: "76px",
                    textShadow: "0 4px 14px rgba(0,0,0,0.5)",
                    fontFamily: "'DM Sans', sans-serif",
                    whiteSpace: "nowrap",
                  }}
                >
                  Invest Beyond Borders with
                </Text>
                <Text
                  sx={{
                    fontSize: 64,
                    fontWeight: 800,
                    color: "#fff",
                    lineHeight: "76px",
                    textShadow: "0 4px 14px rgba(0,0,0,0.5)",
                    fontFamily: "'DM Sans', sans-serif",
                    whiteSpace: "nowrap",
                  }}
                >
                  AI-Powered Global Research
                </Text>
              </Box>
              <Text
                sx={{
                  fontSize: 20,
                  color: "rgba(255,255,255,0.72)",
                  lineHeight: "30px",
                  textShadow: "0 2px 8px rgba(0,0,0,0.4)",
                  width: 640,
                  maxWidth: "100%",
                  fontFamily: "'DM Sans', sans-serif",
                }}
              >
                Discover investment opportunities across the world&apos;s leading markets with
                institutional-grade research, AI-powered insights, and global market
                intelligence—all from one intelligent platform.
              </Text>
            </Stack>

            <Button
              onClick={handleStart}
              endIcon={<ArrowIcon />}
              sx={{
                position: "absolute",
                left: 0,
                top: 298,
                height: 60,
                px: 4,
                borderRadius: "14px",
                textTransform: "none",
                fontWeight: 700,
                fontSize: 16,
                lineHeight: "21px",
                color: "#fff",
                background: CTA_GRADIENT,
                border: "1px solid rgba(255,255,255,0.2)",
                boxShadow:
                  "0 6px 18px rgba(236,132,64,0.2), 0 14px 34px rgba(0,0,0,0.4)",
                "&:hover": { background: CTA_GRADIENT, opacity: 0.95 },
                "& .MuiButton-endIcon": { ml: "12px", mr: 0 },
              }}
            >
              Start Investing
            </Button>
          </Box>

          {/* Benefit tray — Figma 8915:33187 */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={2}
            sx={{
              position: "absolute",
              left: 60,
              top: 510,
              width: 651,
              height: 60,
              px: "20px",
              py: "16px",
              boxSizing: "border-box",
              borderRadius: "16px",
              bgcolor: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.12)",
              boxShadow: "0 12px 24px rgba(0,0,0,0.2)",
              backdropFilter: "blur(8px)",
              zIndex: 2,
            }}
          >
            {BENEFIT_TRAY.map((item, index) => (
              <React.Fragment key={item.label}>
                {index > 0 ? (
                  <Box
                    sx={{
                      width: "1px",
                      height: 28,
                      bgcolor: "rgba(255,255,255,0.2)",
                      flexShrink: 0,
                    }}
                  />
                ) : null}
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ flex: 1, minWidth: 0 }}
                >
                  <Box
                    component="img"
                    src={item.icon}
                    alt=""
                    sx={{ width: 28, height: 28, flexShrink: 0 }}
                  />
                  <Text
                    sx={{
                      fontSize: 11,
                      fontWeight: 500,
                      color: "#FFD2BA",
                      lineHeight: "14px",
                      fontFamily: "'DM Sans', sans-serif",
                    }}
                  >
                    {item.label}
                  </Text>
                </Stack>
              </React.Fragment>
            ))}
          </Stack>

          {/* Compass — Figma 8757:21609 (nudged slightly right) */}
          <Box
            sx={{
              position: "absolute",
              right: 24,
              top: 141,
              width: 384,
              height: 427,
              zIndex: 1,
            }}
          >
            <Box
              sx={{
                position: "absolute",
                left: "50%",
                top: "55%",
                transform: "translate(-50%, -50%)",
                width: 320,
                height: 320,
                borderRadius: "50%",
                background:
                  "radial-gradient(circle, rgba(236,132,64,0.35) 0%, rgba(74,42,26,0.15) 40%, transparent 70%)",
                pointerEvents: "none",
              }}
            />
            <Box
              component="img"
              src={ICONS.hero}
              alt="Jarvis Atlas"
              sx={{
                position: "relative",
                width: "100%",
                height: "100%",
                objectFit: "contain",
                borderRadius: "24px",
                filter: "drop-shadow(0 18px 40px rgba(0,0,0,0.2))",
              }}
            />
          </Box>
        </Box>

        {/* Mobile hero */}
        <Box sx={{ display: { xs: "block", md: "none" }, px: 3, pt: 2.5, pb: 0 }}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.5}
            sx={{ mb: 2.5 }}
          >
            <IconButton
              onClick={handleBack}
              aria-label="Back to Explore"
              sx={{
                width: 42,
                height: 42,
                borderRadius: "12px",
                bgcolor: "rgba(255,255,255,0.2)",
                border: "1px solid #C3C3C3",
                boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)",
                p: 0,
                "&:hover": { bgcolor: "rgba(255,255,255,0.35)" },
              }}
            >
              <ArrowBackRoundedIcon sx={{ fontSize: 20, color: "#fff" }} />
            </IconButton>
            <Stack spacing={0.25}>
              <Text
                sx={{
                  fontSize: 18,
                  fontWeight: 800,
                  color: "#fff",
                  lineHeight: "24px",
                }}
              >
                Jarvis Atlas
              </Text>
              <Text
                sx={{
                  fontSize: 14,
                  fontWeight: 400,
                  color: "rgba(255,255,255,0.7)",
                  lineHeight: "21px",
                }}
              >
                Investing powered by AI
              </Text>
            </Stack>
          </Stack>

          <Stack spacing={1.25}>
            <Text
              sx={{
                fontSize: 30,
                fontWeight: 800,
                color: "#fff",
                lineHeight: "39px",
                textShadow: "0 4px 14px rgba(0,0,0,0.5)",
              }}
            >
              Invest Beyond Borders with AI-Powered Global Research
            </Text>
            <Text
              sx={{
                fontSize: 14,
                color: "rgba(255,255,255,0.72)",
                lineHeight: "21px",
                textShadow: "0 2px 8px rgba(0,0,0,0.4)",
              }}
            >
              Discover investment opportunities across the world&apos;s leading markets with
              institutional-grade research, AI-powered insights, and global market
              intelligence—all from one intelligent platform.
            </Text>
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-end",
                justifyContent: "space-between",
                gap: 1.5,
                mt: 4,
                minHeight: 200,
              }}
            >
              <Box
                component="img"
                src={ICONS.hero}
                alt="Jarvis Atlas"
                sx={{
                  width: 204,
                  height: 220,
                  maxWidth: "55%",
                  objectFit: "cover",
                  objectPosition: "center top",
                  alignSelf: "flex-end",
                  ml: "-20px",
                  mb: "-24px",
                }}
              />
              <Button
                onClick={handleStart}
                endIcon={<ArrowIcon size={20} />}
                sx={{
                  flexShrink: 0,
                  mb: 2.5,
                  height: 48,
                  px: 2,
                  borderRadius: "14px",
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: 14,
                  color: "#fff",
                  whiteSpace: "nowrap",
                  background: CTA_GRADIENT,
                  border: "1px solid rgba(255,255,255,0.2)",
                  boxShadow: "0 10px 12px rgba(236,132,64,0.25)",
                  "&:hover": { background: CTA_GRADIENT, opacity: 0.95 },
                  "& .MuiButton-endIcon": { ml: "8px", mr: 0 },
                }}
              >
                Start Investing
              </Button>
            </Box>
          </Stack>
        </Box>
      </Box>

      {/* About Atlas — desktop (Figma 8757:21619) */}
      <Box sx={{ display: { xs: "none", md: "block" }, px: "60px", py: "60px" }}>
        <Box
          sx={{
            maxWidth: 1320,
            mx: "auto",
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: "48px",
          }}
        >
          <Text
            sx={{
              width: "100%",
              textAlign: "center",
              fontSize: 56,
              fontWeight: 700,
              color: "#000",
              letterSpacing: "-1.12px",
              lineHeight: "64px",
              fontFamily: "'DM Sans', sans-serif",
            }}
          >
            About Atlas
          </Text>

          <Stack spacing="16px" sx={{ width: "100%" }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "minmax(0, 875fr) minmax(0, 436fr)",
                gap: "16px",
                width: "100%",
                alignItems: "stretch",
              }}
            >
              {/* Figma 8757:21624 — hero-card */}
              <Box
                sx={{
                  height: 236,
                  minWidth: 0,
                  px: "32px",
                  pt: "28px",
                  pb: "24px",
                  borderRadius: "20px",
                  bgcolor: MARKETS_CARD_BG,
                  border: "1px solid #3A2A1F",
                  boxShadow:
                    "0 10px 24px rgba(156,67,12,0.2), 0 18px 40px rgba(0,0,0,0.4)",
                  position: "relative",
                  boxSizing: "border-box",
                  display: "flex",
                  flexDirection: "column",
                  overflow: "visible",
                }}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ height: 32, flexShrink: 0 }}
                >
                  <Box
                    component="img"
                    src={ICONS.frameWhite}
                    alt=""
                    sx={{ width: 32, height: 32, display: "block" }}
                  />
                  <Box
                    sx={{
                      bgcolor: "#2A1B14",
                      border: "1px solid #5A3A26",
                      px: "16px",
                      py: "4px",
                      borderRadius: "14px",
                      boxShadow: "0 8px 9px rgba(0,0,0,0.4)",
                    }}
                  >
                    <Text
                      sx={{
                        fontSize: 12,
                        fontWeight: 500,
                        color: "#FDE68A",
                        whiteSpace: "nowrap",
                        fontFamily: "'DM Sans', sans-serif",
                        lineHeight: "normal",
                      }}
                    >
                      Currently AI under reinforcement stage
                    </Text>
                  </Box>
                </Stack>
                <Stack spacing="10px" sx={{ mt: "20px", minWidth: 0, flex: 1 }}>
                  <Text
                    sx={{
                      fontSize: { md: 80, lg: 96 },
                      fontWeight: 700,
                      color: "#fff",
                      lineHeight: 1,
                      letterSpacing: "-1.92px",
                      whiteSpace: "nowrap",
                      fontFamily: "'DM Sans', sans-serif",
                    }}
                  >
                    10+ Markets
                  </Text>
                  <Text
                    sx={{
                      width: "100%",
                      fontSize: 24,
                      fontWeight: 500,
                      color: "#fff",
                      lineHeight: "26px",
                      fontFamily: "'DM Sans', sans-serif",
                      whiteSpace: "nowrap",
                    }}
                  >
                    AI-powered research across the world&apos;s leading financial markets.
                  </Text>
                </Stack>
              </Box>
              <FeatureCard {...FEATURE_CARDS[0]} sx={{ height: 236 }} />
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                gap: "16px",
                width: "100%",
                alignItems: "stretch",
              }}
            >
              {middleFeatures.slice(0, 3).map((item) => (
                <FeatureCard key={item.icon} {...item} sx={{ height: 186 }} />
              ))}
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                gap: "16px",
                width: "100%",
                alignItems: "stretch",
              }}
            >
              {middleFeatures.slice(3).map((item) => (
                <FeatureCard key={item.icon} {...item} sx={{ height: 186 }} />
              ))}
            </Box>

            <FeatureCard {...fullWidthFeature} sx={{ height: 134 }} />
          </Stack>
        </Box>
      </Box>

      {/* About Atlas — mobile */}
      <Box sx={{ px: 3, pt: 2.5, pb: 1.25, display: { xs: "block", md: "none" } }}>
        <Stack
          spacing={2}
          sx={{
            p: 2.5,
            borderRadius: "20px",
            bgcolor: "#fff",
            border: "1px solid #E2E8F0",
            boxShadow: "0 4px 6px rgba(0,0,0,0.04)",
          }}
        >
          <Text sx={{ fontSize: 18, fontWeight: 700, color: "#0F172A", lineHeight: "24px" }}>
            About Atlas
          </Text>
          <Box sx={{ borderBottom: "1px solid #E2E8F0" }} />
          <Stack spacing={2.5}>
            {aboutPreview.map((item, index) => (
              <MobileAboutRow key={`about-preview-${index}`} {...item} />
            ))}
          </Stack>
          <Collapse in={aboutExpanded}>
            <Stack spacing={2.5} sx={{ pt: 2.5 }}>
              {aboutRest.map((item, index) => (
                <MobileAboutRow key={`about-rest-${index}`} {...item} />
              ))}
            </Stack>
          </Collapse>
          <Box
            component="button"
            type="button"
            onClick={() => setAboutExpanded((v) => !v)}
            sx={{
              border: 0,
              bgcolor: "transparent",
              cursor: "pointer",
              p: 0,
              alignSelf: "flex-start",
            }}
          >
            <Text sx={{ fontSize: 14, fontWeight: 500, color: "#2977EC", lineHeight: "24px" }}>
              {aboutExpanded ? "read less" : "read more"}
            </Text>
          </Box>
        </Stack>
      </Box>

      {/* Is This For You — desktop */}
      <Box
        sx={{
          display: { xs: "none", md: "block" },
          py: 12,
          px: 10,
          bgcolor: "#F8FAFC",
          position: "relative",
          overflow: "hidden",
          backgroundImage: `
            linear-gradient(rgba(156,67,12,0.04) 1px, transparent 1px),
            linear-gradient(90deg, rgba(156,67,12,0.04) 1px, transparent 1px)
          `,
          backgroundSize: "240px 180px",
        }}
      >
        <Stack
          direction="row"
          spacing={8}
          alignItems="center"
          sx={{ maxWidth: 1280, mx: "auto", position: "relative" }}
        >
          <Stack spacing={4} sx={{ flex: "0 1 520px" }}>
            <Box
              component="img"
              src={ICONS.anchor}
              alt=""
              sx={{ width: 72, height: 72, objectFit: "contain" }}
            />
            <Text
              sx={{
                fontSize: 80,
                fontWeight: 900,
                color: "#111827",
                lineHeight: "88px",
                letterSpacing: "-1.6px",
              }}
            >
              Is This For You?
            </Text>
            <Text sx={{ fontSize: 18, color: "#6B7280", lineHeight: "28px" }}>
              A quick checklist to help determine whether Jarvis Atlas aligns with your global
              investing goals.
            </Text>
          </Stack>

          <Box
            sx={{
              flex: 1,
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 2,
              width: "100%",
            }}
          >
            {FOR_YOU_CARDS.map((card) => (
              <ChecklistCard key={card.label} {...card} />
            ))}
          </Box>
        </Stack>
      </Box>

      {/* Is This For You — mobile */}
      <Box sx={{ px: 3, py: 1.25, display: { xs: "block", md: "none" } }}>
        <Stack
          spacing={2.5}
          sx={{
            p: 3,
            borderRadius: "24px",
            overflow: "hidden",
            position: "relative",
            border: "1px solid rgba(255,255,255,0.15)",
            background: FOR_YOU_GRADIENT,
            boxShadow:
              "0 0 18px rgba(229,116,33,0.15), 0 10px 24px -10px rgba(66,40,16,0.3), 0 18px 40px -12px rgba(0,0,0,0.15)",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: -110,
              right: -90,
              width: 260,
              height: 260,
              borderRadius: "50%",
              background: "radial-gradient(circle, rgba(229,116,33,0.45) 0%, transparent 70%)",
              pointerEvents: "none",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              bottom: -120,
              right: 40,
              width: 220,
              height: 220,
              borderRadius: "50%",
              background: "radial-gradient(circle, rgba(192,106,38,0.4) 0%, transparent 70%)",
              pointerEvents: "none",
            }}
          />
          <Stack spacing={1.25} sx={{ position: "relative" }}>
            <Text sx={{ fontSize: 18, fontWeight: 700, color: "#E6E6E6", lineHeight: "24px" }}>
              Is This For You?
            </Text>
            <Text sx={{ fontSize: 13, color: "rgba(255,255,255,0.8)", lineHeight: "18px" }}>
              A quick checklist to help determine whether Jarvis Atlas aligns with your global
              investing goals.
            </Text>
          </Stack>
          <Box sx={{ borderBottom: "1px solid rgba(255,255,255,0.2)", position: "relative" }} />
          <Stack spacing={1.5} sx={{ position: "relative" }}>
            {MOBILE_FOR_YOU.map((text) => (
              <MobileForYouRow key={text} text={text} />
            ))}
          </Stack>
        </Stack>
      </Box>

      {/* Quick Facts — desktop */}
      <Box
        sx={{
          display: { xs: "none", md: "block" },
          py: 10,
          px: 7.5,
          bgcolor: "#05070A",
        }}
      >
        <Box sx={{ maxWidth: 1352, mx: "auto" }}>
          <Text
            sx={{
              textAlign: "center",
              fontSize: 44,
              fontWeight: 800,
              color: "#F8FAFC",
              lineHeight: "57px",
              mb: 7,
            }}
          >
            Quick Facts
          </Text>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr",
              gap: 3,
            }}
          >
            {QUICK_FACTS.map((fact) => (
              <FactCard key={`${fact.label}-${fact.value}`} {...fact} />
            ))}
          </Box>
        </Box>
      </Box>

      {/* Quick Facts — mobile */}
      <Box sx={{ px: 3, py: 1.25, display: { xs: "block", md: "none" } }}>
        <Stack
          spacing={2.5}
          sx={{
            p: 3,
            borderRadius: "20px",
            bgcolor: "#fff",
            border: "1px solid #E2E8F0",
            boxShadow: "0 4px 6px rgba(0,0,0,0.04)",
          }}
        >
          <Text sx={{ fontSize: 18, fontWeight: 700, color: "#0F172A", lineHeight: "26px" }}>
            Quick Facts
          </Text>
          <Box sx={{ borderBottom: "1px solid #E2E8F0" }} />
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
            {QUICK_FACTS.map((fact) => (
              <MobileFactTile key={`${fact.label}-${fact.value}`} {...fact} />
            ))}
          </Box>
        </Stack>
      </Box>

      {/* Pricing */}
      <Box
        sx={{
          px: { xs: 2, md: 10 },
          py: { xs: 2.5, md: 12 },
          bgcolor: { xs: "#fff", md: "#F9FAFB" },
          maxWidth: "100%",
        }}
      >
        <Box sx={{ maxWidth: 1320, mx: "auto", width: "100%" }}>
          <Box sx={{ display: { xs: "none", md: "block" }, mb: 5 }}>
            <Stack spacing={1.5} alignItems="center">
              <Text
                sx={{
                  textAlign: "center",
                  fontSize: 52,
                  fontWeight: 800,
                  color: "#05070A",
                  lineHeight: "68px",
                  whiteSpace: "nowrap",
                }}
              >
                Pricings
              </Text>
              <Text
                sx={{
                  textAlign: "center",
                  fontSize: 20,
                  fontWeight: 400,
                  color: "#94A3B8",
                  lineHeight: "26px",
                  whiteSpace: "nowrap",
                }}
              >
                Choose the plan that fits your investing style
              </Text>
            </Stack>
          </Box>
          <AtlasPricingSection plans={plans} onUnlock={handleStart} />
        </Box>
      </Box>

      <AllInOneFooter />
    </Box>
  );
};

export default AtlasProductInfo;
